import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Mail } from "lucide-react";

import LockIcon from "@mui/icons-material/Lock";
import { getQueryFromUrl } from "@utils/helpers/getQueryFromUrl";
import {
  registerToBusinessGroup,
  setAlert,
  useRegisterToBusinessGroup,
} from "@lib/stores";
import { AlertType } from "@lib/stores/common/types";
import { AppDownload } from "@components/layouts/b2c/onboarding/AppDownload";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@ui/card";
import TranslatedText from "@components/items/display/TranslatedText";
import FormTextInput from "@components/items/inputs/FormTextInput";
import { FieldType } from "@utils/types/components";
import { emailRegex } from "@utils/helpers/emailValidation";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import { translate } from "@modules/localizations/translate";

type Inputs = {
  email: string;
  password: string;
  passwordConfirm: string;
};

export const RegisterToBusinessGroup = () => {
  const voucherCode = getQueryFromUrl("voucherCode");

  const [isPasswordSet, setIsPasswordSet] = useState(false);

  const { status } = useRegisterToBusinessGroup();

  const {
    handleSubmit,
    control,
    formState: { isDirty, errors },
  } = useForm<Inputs>({
    defaultValues: {
      email: "",
      password: "",
      passwordConfirm: "",
    },
    mode: "all",
  });

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    const { email, password, passwordConfirm } = data;

    if (password !== passwordConfirm) {
      setAlert({
        show: true,
        for: "page",
        type: AlertType.WARNING,
        messages: [
          {
            code: 0,
            field: null,
            message: translate("onboarding.inputs.errors.passwords mismatch"),
          },
        ],
      });

      return;
    }

    if (voucherCode) {
      registerToBusinessGroup(
        {
          email,
          password,
          businessGroupVoucher: voucherCode,
        },
        () => {
          setIsPasswordSet(true);
        }
      );
    }
  };

  const isDisabled =
    !isDirty || Object.keys(errors).length !== 0 || status === "loading";

  return (
    <Card className="flex h-auto w-[434px] flex-col items-center justify-center rounded-xl border-none bg-dark_background py-3 text-white">
      {!isPasswordSet ? (
        <div className="flex flex-col items-center justify-center space-y-6">
          <CardHeader>
            <CardTitle className="text-[20px] font-medium">
              <TranslatedText text={`onboarding.titles.create an account`} />
            </CardTitle>
          </CardHeader>
          <form
            className="mx-auto flex w-full flex-col space-y-2 px-4 sm:w-[375px] sm:px-0"
            onSubmit={handleSubmit(onSubmit)}
          >
            <CardContent className="mb-2 flex w-full flex-col items-center justify-center gap-3 pb-2">
              <FormTextInput
                inputExtraClassNames="placeholder:rtl:pr-1 placeholder:ltr:pl-1 !bg-[#D9D9D9]"
                control={control}
                name={`email`}
                fieldMeta={{
                  fieldType: FieldType.Text,
                  placeHolder: "name@gmail.com",
                  fieldOptions: { pattern: emailRegex() },
                  required: true,
                }}
                required
                inputHtmlType="email"
                prefix={
                  <Mail className="h-6 w-6 fill-primary text-[#D9D9D9]" />
                }
                validationErrorMessages={{
                  pattern: "invalid email",
                }}
                onEnter={() => {
                  handleSubmit(onSubmit)();
                }}
                readOnly
              />
              <FormTextInput
                inputExtraClassNames="!bg-[#D9D9D9]"
                control={control}
                name={`password`}
                fieldMeta={{
                  fieldType: FieldType.Text,
                  fieldOptions: { minLength: 8 },
                  required: true,
                }}
                inputHtmlType="password"
                prefix={<LockIcon className="text-primary" />}
                validationErrorMessages={{
                  minLength: "The password must be at least 8 characters",
                }}
                onEnter={() => {
                  handleSubmit(onSubmit)();
                }}
              />
              <FormTextInput
                inputExtraClassNames="!bg-[#D9D9D9]"
                control={control}
                name={`passwordConfirm`}
                fieldMeta={{
                  fieldType: FieldType.Text,
                  fieldOptions: { minLength: 8 },
                  required: true,
                }}
                inputHtmlType="password"
                prefix={<LockIcon className="text-primary" />}
                validationErrorMessages={{
                  minLength: "The password must be at least 8 characters",
                }}
                onEnter={() => {
                  handleSubmit(onSubmit)();
                }}
              />
            </CardContent>
            <CardFooter>
              <div className="flex w-full items-center justify-center gap-5">
                <ShadcnButton
                  disabled={isDisabled}
                  className="px-6 text-white"
                  onClick={handleSubmit(onSubmit)}
                  isLoading={status === "loading"}
                >
                  <TranslatedText
                    text={`onboarding.buttons.create`}
                    className="text-[16px] font-normal leading-[22px]"
                  />
                </ShadcnButton>
              </div>
            </CardFooter>
          </form>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center space-y-6">
          <CardHeader>
            <CardTitle className="text-[20px] font-medium">
              <TranslatedText
                text={`onboarding.titles.your account has been created successfully!`}
              />
            </CardTitle>
          </CardHeader>
          <a href="https://wajeez.page.link/invite">
            <ShadcnButton className="px-6 text-white">
              <TranslatedText
                text={`onboarding.buttons.go to the app`}
                className="text-[16px] font-normal leading-[22px]"
              />
            </ShadcnButton>
          </a>
        </div>
      )}
      <AppDownload />
    </Card>
  );
};
